import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileOutput, Loader2 } from 'lucide-react';
import { useSetAtom } from 'jotai';
import type { Report } from 'powerbi-client';
import { toastAtom } from '../../atoms/toastAtom';
import { ToastVariant } from '../../constants/toast-variant';
import {
  PowerBiExportFileFormat,
  usePowerBiExport,
} from '../../hooks/usePowerBiExport';
import { PdfFileIcon } from './icons/pdf-file-icon.svg';
import { PptxFileIcon } from './icons/pptx-file-icon.svg';
import styles from '../power-bi/power-bi-page.module.scss';

interface PowerBiExportViewProps {
  appName: string;
  report: Report | null;
  embedReady: boolean;
}

export function PowerBiExportView({
  appName,
  report,
  embedReady,
}: PowerBiExportViewProps) {
  const [showMenu, setShowMenu] = useState(false);
  const setToast = useSetAtom(toastAtom);
  const {
    handleExport,
    isExportInProgress,
    exportFailureReason,
    exportProgress,
  } = usePowerBiExport(appName);

  // The export can fail asynchronously while polling for status (after
  // handleExport's promise has already resolved), so surface it separately.
  useEffect(() => {
    if (!exportFailureReason) return;
    setToast({ message: exportFailureReason, variant: ToastVariant.ERROR });
  }, [exportFailureReason, setToast]);

  const handleSelectFormat = async (
    fileFormat: PowerBiExportFileFormat,
    currentPageOnly = false
  ) => {
    setShowMenu(false);
    try {
      await handleExport(report, fileFormat, currentPageOnly);
    } catch (err) {
      setToast({
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Failed to export report. Please try again.',
        variant: ToastVariant.ERROR,
      });
    }
  };

  return (
    <div className={styles['toolbar-btn-wrapper']}>
      <button
        className={`${styles['export-trigger-btn']} ${
          showMenu ? styles['active'] : ''
        } ${isExportInProgress ? styles['exporting'] : ''}`}
        onClick={() => setShowMenu((prev) => !prev)}
        disabled={!embedReady || isExportInProgress}
        style={
          isExportInProgress
            ? ({
                '--export-progress': `${exportProgress}%`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {isExportInProgress ? (
          <span className={styles['export-trigger-content']}>
            <Loader2 size={16} className={styles['spin']} aria-hidden="true" />
            Downloading Report
          </span>
        ) : (
          <span className={styles['export-trigger-content']}>
            <FileOutput size={16} aria-hidden="true" />
            Export Report
            {showMenu ? (
              <ChevronUp size={14} aria-hidden="true" />
            ) : (
              <ChevronDown size={14} aria-hidden="true" />
            )}
          </span>
        )}
      </button>
      {showMenu && (
        <div className={styles['export-dropdown']}>
          <button
            className={styles['export-menu-item']}
            onClick={() => handleSelectFormat('PDF')}
          >
            <PdfFileIcon width="17" height="17" />
            Export to PDF (Entire Report)
          </button>
          <button
            className={styles['export-menu-item']}
            onClick={() => handleSelectFormat('PPTX')}
          >
            <PptxFileIcon width="17" height="17" />
            Export to PPTX (Entire Report)
          </button>
          <button
            className={styles['export-menu-item']}
            onClick={() => handleSelectFormat('PPTX', true)}
          >
            <PptxFileIcon width="17" height="17" />
            Export to PPTX (Current Page)
          </button>
        </div>
      )}
    </div>
  );
}
